import { useState, useEffect } from 'react'
import {
	Box,
	Heading,
	VStack,
	HStack,
	Input,
	Button,
	Card,
	Text,
	Badge,
	Grid,
	Flex,
	Stat,
	Table,
	Center,
} from '@chakra-ui/react'
import { SaleEntity } from '@/entities/api/sales/sales'
import Toast from '@/components/elements/toast'
import { fetchSales } from '@/services/api/sales/sales'
import {
	OrderPaymethodsConvert,
	WompiStatusColorScheme,
} from '@/utils/payments-values'
import { WompiStatusConvert } from '@/utils/wompi-status-convert'
import { OrderPaymethods, WompiStatus } from '@/entities/api/orders/orders'
import formatCurrency from '@/utils/price-convert'
import JwtPayload from '@/entities/api/jwt'
import Layout from '@/components/layout/layout'
import { RootState } from '@/store/store'
import { useSelector } from 'react-redux'
import SpinnerLoading from '@/components/elements/spinner'
import Search from '@/components/elements/search'
import Icon from '@/components/shared/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { API_URL } from '@/services/api/keys'
import {
	formatDate,
	formatDateYYYYMMDD,
	nowBogota,
	parseDateBogota,
} from '@/utils/date'
const Sales = ({ data }: { data: JwtPayload }) => {
	const token = useSelector((state: RootState) => state.auth.token)
	const [sales, setSales] = useState<SaleEntity[]>([])
	const [search, setSearch] = useState<string>('')
	const [isLoading, setIsLoading] = useState(false)
	const [fromDate, setFromDate] = useState('')
	const [toDate, setToDate] = useState('')

	useEffect(() => {
		const now = nowBogota()
		const lastMonth = new Date(now)
		lastMonth.setMonth(now.getMonth() - 1)

		setFromDate(formatDateYYYYMMDD(lastMonth))
		setToDate(formatDateYYYYMMDD(now))
	}, [])

	useEffect(() => {
		const loadSales = async () => {
			setIsLoading(true)
			try {
				const fromDateTime = parseDateBogota(fromDate, true)
				const toDateTime = parseDateBogota(toDate, false)

				const response = await fetchSales(
					token ?? '',
					formatDateYYYYMMDD(fromDateTime),
					formatDateYYYYMMDD(toDateTime)
				)

				if (response.status === 200) {
					setSales(response.data)
					Toast({
						type: 'success',
						message: `Se cargaron ${response.data.length} ventas`,
					})
				} else {
					throw new Error('Error al cargar las ventas')
				}
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar las ventas',
				})
			} finally {
				setIsLoading(false)
			}
		}

		if (toDate && fromDate) {
			loadSales()
		}
	}, [toDate, fromDate])

	const filteredSales = sales.filter(
		(sale) =>
			sale.customerName.toLowerCase().includes(search.toLowerCase()) ||
			sale.orderCode.toLowerCase().includes(search.toLowerCase()) ||
			sale.customerEmail.toLowerCase().includes(search.toLowerCase())
	)

	const totalRevenue = sales.reduce((sum, sale) => sum + sale.total, 0)
	const totalOrders = sales.length
	const totalProducts = sales.reduce((sum, sale) => sum + sale.totalQuantity, 0)
	const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0

	const getPaymentStatusColor = (status: WompiStatus) => {
		return WompiStatusColorScheme(status)
	}

	const getPaymentStatusText = (status: WompiStatus) => {
		return WompiStatusConvert(status)
	}

	const getPaymentMethodText = (method: OrderPaymethods) => {
		return OrderPaymethodsConvert(method)
	}

	const handleDownloadBill = async (billUrl: string, orderCode: string) => {
		try {
			if (!billUrl) {
				Toast({
					type: 'error',
					message: 'URL de factura no disponible',
				})
				return
			}

			const link = document.createElement('a')
			link.href = `${API_URL}/${billUrl}`
			link.target = '_blank'
			link.download = `factura-${orderCode}.pdf`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			Toast({
				type: 'success',
				message: 'Descarga de factura iniciada',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al descargar la factura',
			})
		}
	}

	const formatPhone = (phone: number) => {
		const phoneStr = phone.toString()
		if (phoneStr.length === 10) {
			return phoneStr.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
		}
		return phoneStr
	}

	return (
		<Layout data={data}>
			{isLoading ? (
				<SpinnerLoading color='black' size='xl' />
			) : (
				<>
					<Flex
						w='full'
						p={{ base: '2', sm: '3', md: '4', lg: '4' }}
						alignItems='center'
						flexDirection={{ base: 'column', md: 'row', lg: 'row' }}
						justifyContent={{
							base: 'start',
							md: 'space-between',
							lg: 'space-between',
						}}
						gap={{ base: '4', sm: '3', md: '0' }}
					>
						<Heading
							as='h2'
							color='black'
							fontSize={{ base: 'xl', sm: '2xl', md: '2xl', lg: '3xl' }}
							textAlign={{ base: 'center', sm: 'center', md: 'start' }}
							whiteSpace='normal'
							wordBreak='break-word'
							fontWeight='800'
							mb={{ base: '2', sm: '0', md: '0' }}
						>
							Ventas
						</Heading>

						<Box
							w={{ base: 'full', sm: 'full', md: 'auto', lg: 'auto' }}
							flexDirection={{ base: 'column', sm: 'column', md: 'row' }}
							display='flex'
							alignItems={{ base: 'stretch', sm: 'center', md: 'center' }}
							justifyContent={{ base: 'start', sm: 'center', md: 'center' }}
							p={{ base: '2', sm: '3', md: '4' }}
							gap={{ base: '3', sm: '3', md: '4' }}
						>
							<Flex
								w={{ base: 'full', sm: 'full', md: 'auto', lg: 'auto' }}
								wrap='wrap'
								gap={{ base: '2', sm: '3', md: '4' }}
								justify={{ base: 'stretch', sm: 'center', md: 'center' }}
								order={{ base: '2', sm: '2', md: '1' }}
							>
								<Box w={{ base: 'full', sm: 'full', md: 'auto' }}>
									<Search setSearch={setSearch} />
								</Box>
							</Flex>

							<Box
								w={{ base: 'full', sm: 'full', md: 'auto' }}
								mb={{ base: '2', sm: '0', md: '0' }}
								order={{ base: '1', sm: '1', md: '2' }}
							>
								<HStack
									gap={{ base: '2', sm: '2', md: '2' }}
									flexWrap={{ base: 'wrap', sm: 'nowrap', md: 'nowrap' }}
									justify={{ base: 'center', sm: 'center', md: 'flex-end' }}
								>
									<Box w={{ base: 'full', sm: 'auto', md: 'auto' }}>
										<Input
											p={{ base: '3', sm: '4', md: '4' }}
											h={{ base: '10', sm: '12', md: '12' }}
											type='date'
											value={fromDate}
											onChange={(e) => setFromDate(e.target.value)}
											size={{ base: 'sm', sm: 'sm', md: 'sm' }}
											bg='white'
											borderColor='gray.200'
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											_hover={{ borderColor: 'blue.300' }}
											_focus={{
												borderColor: 'blue.500',
												boxShadow: '0 0 0 1px blue.500',
											}}
										/>
									</Box>
									<Box w={{ base: 'full', sm: 'auto', md: 'auto' }}>
										<Input
											p={{ base: '3', sm: '4', md: '4' }}
											type='date'
											h={{ base: '10', sm: '12', md: '12' }}
											value={toDate}
											onChange={(e) => setToDate(e.target.value)}
											size={{ base: 'sm', sm: 'sm', md: 'sm' }}
											bg='white'
											borderColor='gray.200'
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											_hover={{ borderColor: 'blue.300' }}
											_focus={{
												borderColor: 'blue.500',
												boxShadow: '0 0 0 1px blue.500',
											}}
										/>
									</Box>
								</HStack>
							</Box>
						</Box>
					</Flex>

					<Box
						w='full'
						px={{ base: '2', sm: '3', md: '4', lg: '4' }}
						mb={{ base: '4', sm: '5', md: '6' }}
					>
						<Grid
							templateColumns={{
								base: '1fr',
								sm: 'repeat(2, 1fr)',
								md: 'repeat(2, 1fr)',
								lg: 'repeat(4, 1fr)',
							}}
							gap={{ base: '3', sm: '3', md: '4', lg: '4' }}
						>
							<Card.Root bg='white' borderWidth='1px' borderColor='gray.200'>
								<Card.Body p={{ base: '3', sm: '4', md: '4' }}>
									<Stat.Root>
										<Stat.Label
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											color='gray.600'
											fontWeight='medium'
										>
											Ingresos Totales
										</Stat.Label>
										<Stat.ValueText
											fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
											fontWeight='bold'
											color='green.600'
										>
											{formatCurrency(totalRevenue)}
										</Stat.ValueText>
									</Stat.Root>
								</Card.Body>
							</Card.Root>
							<Card.Root bg='white' borderWidth='1px' borderColor='gray.200'>
								<Card.Body p={{ base: '3', sm: '4', md: '4' }}>
									<Stat.Root>
										<Stat.Label
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											color='gray.600'
											fontWeight='medium'
										>
											Total Pedidos concretados
										</Stat.Label>
										<Stat.ValueText
											fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
											fontWeight='bold'
											color='blue.600'
										>
											{totalOrders}
										</Stat.ValueText>
									</Stat.Root>
								</Card.Body>
							</Card.Root>
							<Card.Root bg='white' borderWidth='1px' borderColor='gray.200'>
								<Card.Body p={{ base: '3', sm: '4', md: '4' }}>
									<Stat.Root>
										<Stat.Label
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											color='gray.600'
											fontWeight='medium'
										>
											Productos Vendidos
										</Stat.Label>
										<Stat.ValueText
											fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
											fontWeight='bold'
											color='purple.600'
										>
											{totalProducts}
										</Stat.ValueText>
									</Stat.Root>
								</Card.Body>
							</Card.Root>
							<Card.Root bg='white' borderWidth='1px' borderColor='gray.200'>
								<Card.Body p={{ base: '3', sm: '4', md: '4' }}>
									<Stat.Root>
										<Stat.Label
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											color='gray.600'
											fontWeight='medium'
										>
											Valor Promedio
										</Stat.Label>
										<Stat.ValueText
											fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
											fontWeight='bold'
											color='orange.600'
										>
											{formatCurrency(avgOrderValue)}
										</Stat.ValueText>
									</Stat.Root>
								</Card.Body>
							</Card.Root>
						</Grid>
					</Box>

					<Box
						w='full'
						px={{ base: '2', sm: '4', md: '6', lg: '6' }}
						pb={{ base: '4', sm: '5', md: '6' }}
					>
						<Card.Root
							bg='white'
							borderWidth='1px'
							borderColor='gray.200'
							shadow='lg'
							borderRadius={{ base: 'lg', sm: 'xl', md: 'xl' }}
							overflow='hidden'
						>
							<Card.Header
								bg='gray.50'
								p={{ base: '4', sm: '5', md: '6' }}
								borderBottomWidth='1px'
								borderColor='gray.200'
							>
								<HStack
									justify='space-between'
									align='center'
									flexWrap={{ base: 'wrap', sm: 'nowrap', md: 'nowrap' }}
									gap={{ base: '2', sm: '0', md: '0' }}
								>
									<VStack
										align={{ base: 'center', sm: 'start', md: 'start' }}
										gap='1'
									>
										<Text
											color='gray.600'
											fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
											textAlign={{ base: 'center', sm: 'left', md: 'left' }}
										>
											{filteredSales.length} ventas encontradas
										</Text>
									</VStack>
									<Badge
										colorScheme='blue'
										size={{ base: 'md', sm: 'lg', md: 'lg' }}
										px={{ base: '2', sm: '3', md: '3' }}
										py='1'
										borderRadius='full'
										fontWeight='bold'
										fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
									>
										{filteredSales.length} resultados
									</Badge>
								</HStack>
							</Card.Header>

							<Card.Body p='0'>
								{filteredSales.length === 0 ? (
									<Center p={{ base: '8', sm: '10', md: '12' }}>
										<VStack gap={{ base: '4', sm: '5', md: '6' }}>
											<Box
												bg='gray.100'
												p={{ base: '4', sm: '5', md: '6' }}
												borderRadius='full'
												display='flex'
												alignItems='center'
												justifyContent='center'
											>
												<Icon icon='search' size='lg' color='gray.400' />
											</Box>
											<VStack gap='2' textAlign='center'>
												<Heading
													size={{ base: 'md', sm: 'lg', md: 'lg' }}
													color='gray.600'
												>
													No se encontraron ventas
												</Heading>
												<Text
													color='gray.500'
													fontSize={{ base: 'sm', sm: 'md', md: 'md' }}
													maxW='md'
													px={{ base: '4', sm: '0', md: '0' }}
												>
													Ajusta los filtros de búsqueda o el rango de fechas
													para encontrar las ventas que buscas
												</Text>
											</VStack>
										</VStack>
									</Center>
								) : (
									<Box overflowX='auto'>
										<Table.Root
											minW='1024px'
											size={{ base: 'md', sm: 'md', md: 'md' }}
											variant='line'
											p={4}
										>
											<Table.Header
												bg='gray.50'
												position='sticky'
												top='0'
												zIndex='1'
												textAlign='center'
											>
												<Table.Row>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textTransform='uppercase'
														textAlign='center'
														letterSpacing='wide'
														minW={{ base: '80px', sm: '100px', md: '120px' }}
													>
														Código
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textTransform='uppercase'
														letterSpacing='wide'
														minW={{ base: '120px', sm: '150px', md: '180px' }}
													>
														Cliente
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														textAlign='center'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textTransform='uppercase'
														letterSpacing='wide'
														minW={{ base: '100px', sm: '120px', md: '140px' }}
													>
														Fecha
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textTransform='uppercase'
														letterSpacing='wide'
														textAlign='center'
														minW={{ base: '100px', sm: '120px', md: '140px' }}
													>
														Productos
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textTransform='uppercase'
														textAlign='center'
														letterSpacing='wide'
														minW={{ base: '100px', sm: '120px', md: '140px' }}
													>
														Método Pago
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textAlign='center'
														textTransform='uppercase'
														letterSpacing='wide'
														minW={{ base: '80px', sm: '100px', md: '120px' }}
													>
														Estado
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textAlign='center'
														textTransform='uppercase'
														letterSpacing='wide'
														minW={{ base: '80px', sm: '100px', md: '120px' }}
													>
														Total
													</Table.ColumnHeader>
													<Table.ColumnHeader
														color='gray.700'
														fontWeight='bold'
														fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
														py={{ base: '3', sm: '4', md: '4' }}
														textAlign='center'
														textTransform='uppercase'
														letterSpacing='wide'
														minW={{ base: '60px', sm: '80px', md: '100px' }}
													>
														Acciones
													</Table.ColumnHeader>
												</Table.Row>
											</Table.Header>
											<Table.Body>
												{filteredSales.map((sale, index) => (
													<Table.Row
														key={sale.orderCode}
														bg={index % 2 === 0 ? 'white' : 'gray.50'}
														borderLeftWidth='4px'
														borderLeftColor='transparent'
													>
														<Table.Cell
															py={{ base: '3', sm: '4', md: '4' }}
															display='flex'
															justifyContent='center'
															alignItems='center'
														>
															<Tooltip content={`Orden: ${sale.orderCode}`}>
																<Box
																	bg='blue.100'
																	px={{ base: '2', sm: '3', md: '3' }}
																	py={{ base: '1', sm: '2', md: '2' }}
																	borderRadius='lg'
																	display='inline-block'
																>
																	<Text
																		fontWeight='bold'
																		color='blue.700'
																		fontSize={{
																			base: 'xs',
																			sm: 'sm',
																			md: 'sm',
																		}}
																		fontFamily='mono'
																	>
																		{sale.orderCode}
																	</Text>
																</Box>
															</Tooltip>
														</Table.Cell>
														<Table.Cell py={{ base: '3', sm: '4', md: '4' }}>
															<VStack
																align='start'
																gap={{ base: '1', sm: '2', md: '2' }}
															>
																<Text
																	fontWeight='bold'
																	fontSize={{ base: 'sm', sm: 'md', md: 'md' }}
																	color='gray.800'
																>
																	{sale.customerName}
																</Text>
																<HStack gap='2'>
																	<Icon
																		icon='email'
																		size='xs'
																		color='gray.400'
																	/>
																	<Text
																		color='gray.600'
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																	>
																		{sale.customerEmail}
																	</Text>
																</HStack>
																<HStack gap='2'>
																	<Icon
																		icon='phone'
																		size='xs'
																		color='gray.400'
																	/>
																	<Text
																		color='gray.600'
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																	>
																		{formatPhone(sale.customerPhone)}
																	</Text>
																</HStack>
															</VStack>
														</Table.Cell>
														<Table.Cell py={{ base: '3', sm: '4', md: '4' }}>
															<VStack align='start' gap='1'>
																<Text
																	fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
																	fontWeight='medium'
																	color='gray.800'
																>
																	{formatDate(sale.createdDate.toString())}
																</Text>
															</VStack>
														</Table.Cell>
														<Table.Cell py={{ base: '3', sm: '4', md: '4' }}>
															<VStack
																align='start'
																gap={{ base: '1', sm: '2', md: '2' }}
															>
																<HStack gap='2'>
																	<Badge
																		colorScheme='purple'
																		size={{ base: 'sm', sm: 'sm', md: 'sm' }}
																		borderRadius='full'
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																	>
																		{sale.productsCount} productos
																	</Badge>
																</HStack>
																<Text
																	fontSize={{ base: '2xs', sm: 'xs', md: 'xs' }}
																	color='gray.600'
																>
																	<strong>Cantidad:</strong>{' '}
																	{sale.totalQuantity} unidades
																</Text>
																{sale.products && sale.products.length > 0 && (
																	<Text
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																		color='gray.500'
																	>
																		{sale.products[0].name}
																		{sale.products.length > 1 &&
																			` +${sale.products.length - 1} más`}
																	</Text>
																)}
															</VStack>
														</Table.Cell>
														<Table.Cell py={{ base: '3', sm: '4', md: '4' }}>
															<Badge
																colorScheme='gray'
																size={{ base: 'sm', sm: 'md', md: 'md' }}
																px={{ base: '2', sm: '3', md: '3' }}
																py='1'
																borderRadius='full'
																fontWeight='medium'
																fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }}
															>
																{getPaymentMethodText(sale.paymentMethod)}
															</Badge>
														</Table.Cell>
														<Table.Cell py={{ base: '3', sm: '4', md: '4' }}>
															<Badge
																colorScheme={getPaymentStatusColor(
																	sale.paymentStatus
																)}
																size={{ base: 'sm', sm: 'md', md: 'md' }}
																px={{ base: '2', sm: '3', md: '3' }}
																py={{ base: '1', sm: '2', md: '2' }}
																borderRadius='full'
																fontWeight='bold'
																textTransform='uppercase'
																letterSpacing='wide'
																fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }}
															>
																{getPaymentStatusText(sale.paymentStatus)}
															</Badge>
														</Table.Cell>
														<Table.Cell
															textAlign='right'
															py={{ base: '3', sm: '4', md: '4' }}
														>
															<VStack
																align='end'
																gap={{ base: '1', sm: '2', md: '2' }}
															>
																<Text
																	fontWeight='black'
																	fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
																	color='green.600'
																>
																	{formatCurrency(sale.total)}
																</Text>
																{sale.totalDiscount > 0 && (
																	<Badge
																		colorScheme='green'
																		size={{ base: 'sm', sm: 'sm', md: 'sm' }}
																		borderRadius='full'
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																	>
																		-{formatCurrency(sale.totalDiscount)}
																	</Badge>
																)}
																{sale.totalShipping > 0 && (
																	<Text
																		fontSize={{
																			base: '2xs',
																			sm: 'xs',
																			md: 'xs',
																		}}
																		color='gray.500'
																	>
																		+ {formatCurrency(sale.totalShipping)} envío
																	</Text>
																)}
															</VStack>
														</Table.Cell>
														<Table.Cell
															textAlign='center'
															py={{ base: '3', sm: '4', md: '4' }}
														>
															<Tooltip content='Descargar Factura'>
																<Button
																	size={{ base: 'sm', sm: 'md', md: 'md' }}
																	colorScheme='blue'
																	variant='ghost'
																	onClick={() =>
																		handleDownloadBill(
																			sale.billUrl,
																			sale.orderCode
																		)
																	}
																	disabled={!sale.billUrl}
																	borderRadius='lg'
																>
																	<Icon icon='download' size='lg' />
																</Button>
															</Tooltip>
														</Table.Cell>
													</Table.Row>
												))}
											</Table.Body>
										</Table.Root>
									</Box>
								)}
							</Card.Body>
						</Card.Root>
					</Box>

					{filteredSales.length > 0 && (
						<Box
							w='full'
							px={{ base: '2', sm: '3', md: '4' }}
							py={{ base: '3', sm: '4', md: '4' }}
						>
							<Text
								fontSize={{ base: 'xs', sm: 'sm', md: 'sm' }}
								color='gray.500'
								textAlign='center'
							>
								Mostrando {filteredSales.length} de {sales.length} ventas
							</Text>
						</Box>
					)}
				</>
			)}
		</Layout>
	)
}

export default Sales
